// eslint-disable-next-line import/no-webpack-loader-syntax
import katexCss from "raw-loader!katex/dist/katex.min.css";
import { renderLatexToHtml } from "./latexRenderer";

/**
 * ساخت HTML خوداتکا (Self-contained)
 *
 * هدف: خروجی ذخیره‌شده در دیتابیس، در «هر صفحه‌ای» — حتی بدون هیچ تغییر
 * در سورس سایت اصلی — دقیقاً مثل پیش‌نمایش ادیتور نمایش داده شود.
 *
 * سایت اصلی محتوا را با dangerouslySetInnerHTML خام نمایش می‌دهد؛ پس خروجی باید:
 *  ۱) CSS کامل KaTeX + استایل RTL را داخل خودش داشته باشد (<style data-exam-styles>)
 *  ۲) همه فرمول‌ها از قبل با KaTeX رندر شده باشند — چون سایت اصلی JS رندر ندارد
 *
 * فونت‌های KaTeX از CDN رسمی jsdelivr لود می‌شوند (کش‌شدنی، بدون نصب چیزی).
 */

// فونت‌های KaTeX از CDN — باید دقیقاً هم‌نسخه KaTeX نصب‌شده پروژه باشند.
// نسخه اشتباه CSS → ساختار کلاس‌ها فرق دارد (مثلاً position:relative در .katex
// فقط در 0.18+ هست) و خط کسرها جابه‌جا/حذف می‌شود.
import { version as KATEX_VERSION } from "katex";

const KATEX_FONTS_CDN = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist`;
const cssWithFontUrls = katexCss.replace(
  /url\(fonts\//g,
  `url(${KATEX_FONTS_CDN}/fonts/`,
);

/**
 * سخت‌سازی CSS کاتکس در برابر صفحات میزبانی که با !important به همه عناصر
 * font-family/font-size دیکته می‌کنند (مثل force-font-important با 10pt).
 *
 * بدون این، هر span داخلی فرمول جداگانه ۱۰pt می‌شود و زنجیره اندازه‌های نسبی
 * em (که کل چیدمان کسر/رادیکال روی آن است) فرومی‌ریزد.
 *
 * روش: همه‌ی declaration های font-size/font-family بیرون از بلوک‌های @font-face
 * (!important می‌گیرند — descriptor های @font-face نباید دست بخورند).
 * چون استایل ما «بعد از» استایل میزبان در DOM قرار می‌گیرد، در تساوی
 * specificity برنده می‌شود؛ و اغلب جاها specificity هم بالاتر است.
 */
const hardenKatexCss = (css) => {
  const faces = [];
  const masked = css.replace(/@font-face\s*\{[^{}]*\}/g, (m) => {
    faces.push(m);
    return `/*FF${faces.length - 1}*/`;
  });
  const hardened = masked
    .replace(/font-size:\s*([^;}]+)/g, "font-size:$1!important")
    .replace(/font-family:\s*([^;}]+)/g, "font-family:$1!important");
  return hardened.replace(/\/\*FF(\d+)\*\//g, (_, i) => faces[+i]);
};

const hardenedKatexCss = hardenKatexCss(cssWithFontUrls);

const BASE_STYLE = `
<style data-exam-styles>
/* ─── KaTeX (خودکار همراه سوال، سخت‌سازی‌شده علیه فونت تحمیلی میزبان) ─── */
${hardenedKatexCss}

/* ─── قواعد پایه نمایش سوال (پیروی کامل از فونت انتخابی صفحه میزبان) ─── */
.exam-question-content {
  direction: rtl;
  text-align: right;
  font-family: inherit;
  line-height: 1.55;
  font-size: inherit;
  color: #1e293b;
  /* فاصله امن از خطوط افقی بالا و پایین جدول سوالات */
  padding: 4px 2px !important;
  box-sizing: border-box !important;
  /* جلوگیری قطعی از همپوشانی سوالات روی هم:
     هر سوال بلوک مستقل خود را می‌سازد و شناوری تصاویر را پاک می‌کند */
  display: block;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.exam-question-content::after {
  content: "";
  display: table;
  clear: both;
}
.exam-question-content p {
  margin: 0 0 0.35em;
  padding-top: 0;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: inherit;
}
.exam-question-content p:empty {
  min-height: 1.55em;
}
.exam-question-content p:empty::before {
  content: "\\a0";
  visibility: hidden;
}
/* سطرهای چپ‌چین: جهت LTR برای حفظ ترتیب درست فرمول‌ها و عبارات */
.exam-question-content [style*="text-align: left"],
.exam-question-content [style*="text-align:left"] {
  direction: ltr !important;
  text-align: left !important;
  unicode-bidi: isolate !important;
}
.exam-question-content [style*="text-align: right"],
.exam-question-content [style*="text-align:right"] {
  direction: rtl !important;
  text-align: right !important;
}
.exam-question-content h1 { font-size: 1.6rem; font-weight: 800; margin: 1.5rem 0 .75rem; color: #0f172a; }
.exam-question-content h2 { font-size: 1.35rem; font-weight: 700; margin: 1.25rem 0 .5rem; color: #1e293b; }
.exam-question-content h3 { font-size: 1.15rem; font-weight: 700; margin: 1rem 0 .5rem; color: #334155; }
.exam-question-content img { max-width: 100%; height: auto; border-radius: 6px; }
.exam-question-content table { border-collapse: collapse; width: auto; margin: 0.5em 0; direction: rtl; }
.exam-question-content th, .exam-question-content td {
  border: 1px solid #94a3b8; padding: 4px 10px; min-width: 3em; text-align: right;
}
.exam-question-content ul, .exam-question-content ol { padding-right: 1.5em; padding-left: 0; margin: .5em 0; }
.exam-question-content blockquote {
  border-right: 3px solid #cbd5e1; padding-right: 12px; margin-right: 0; color: #475569;
}
/* تصاویر شناور — همان چیدمان ادیتور */
.exam-question-content .exam-image-wrapper { max-width: 60%; }
.exam-question-content .exam-image-wrapper.float-right { float: right; margin: 0 0 12px 16px; }
.exam-question-content .exam-image-wrapper.float-left { float: left; margin: 0 16px 12px 0; }
.exam-question-content .exam-image-wrapper.block-center,
.exam-question-content .exam-image-wrapper.clear-both {
  display: block; margin: 16px auto; text-align: center; clear: both; float: none;
}
.math-formula-container {
  display: inline-block !important;
  direction: ltr !important;
  unicode-bidi: isolate !important;
  vertical-align: baseline !important;
  margin: 0 2px !important;
  line-height: normal !important;
}
.math-formula-container * { direction: ltr !important; }

.math-expression-wrap {
  display: inline-block !important;
  direction: ltr !important;
  unicode-bidi: isolate !important;
  vertical-align: baseline !important;
  margin: 0 4px !important;
  line-height: normal !important;
}
.math-expression-wrap * {
  direction: ltr !important;
}
.math-expression-wrap > * {
  vertical-align: baseline !important;
}

/* ─── فواصل استاندارد داخل فرمول‌ها ─── */
.exam-question-content .katex .mfrac,
.exam-question-content .katex .sqrt {
  margin: 0 2px !important;
}
.exam-question-content .katex .mbin,
.exam-question-content .katex .mrel {
  margin: 0 2px !important;
}

/* ─── خنثی‌سازی فونت تحمیلی میزبان (force-font) روی فرمول‌ها و همسان‌سازی فونت ارقام ─── */
.exam-question-content .katex {
  font-family: inherit !important;
  font-size: 0.88em !important;
  line-height: normal !important;
}
.exam-question-content .katex * {
  font-size: inherit !important;
}
.exam-question-content .katex .sizing.size1 { font-size: 0.7em !important; }
.exam-question-content .katex .sizing.size2 { font-size: 0.85em !important; }
.exam-question-content .katex .sizing.size3,
.exam-question-content .katex .msupsub,
.exam-question-content .katex .msupsub *,
.exam-question-content .katex .msupsub .sizing,
.exam-question-content .katex .msupsub .mord,
.exam-question-content .katex .msupsub .fa-digit { font-size: 1em !important; }
.exam-question-content .katex .sizing.size4 { font-size: 1em !important; }
.exam-question-content .katex .sizing.size5 { font-size: 1em !important; }
.exam-question-content .katex .sizing.size6 { font-size: 1em !important; }
.exam-question-content .katex .sizing.size7 { font-size: 1.2em !important; }
.exam-question-content .katex .sizing.size8 { font-size: 1.44em !important; }
.exam-question-content .katex .sizing.size9 { font-size: 1.73em !important; }
.exam-question-content .katex .sizing.size10 { font-size: 2.07em !important; }
.exam-question-content .katex .sizing.size11 { font-size: 2.49em !important; }

.exam-question-content .katex .fa-digit,
.exam-question-content .fa-digit {
  font-family: inherit !important;
}

/* فاصله امن بین سوالات — اگر سایت اصلی بین سوالات فاصله نگذارد */
.exam-question-content + .exam-question-content { margin-top: 0.5em; }
</style>
`;

/**
 * گروه‌بندی عبارات ریاضی (اعداد، عملگرها و نودهای فرمول) در یک رپر چپ‌چین LTR
 */
export const wrapMathExpressionsInDom = (container) => {
  const FA_LETTERS =
    /[\u0621-\u0628\u062A-\u063A\u0641-\u0642\u0644-\u0648\u064E-\u065F\u067E\u0686\u0698\u06A9\u06AF\u06CC\u0679\u0683\u0688\u0691\u06BA\u06D2\u0629\u0649\u064A\u064B\u064C\u064D\u0650\u0651\u0652]/;
  const OPERATORS = /[+\-−×÷*/=≠±∓<>≤≥≈~≡≅^_%\u00B0′″‰√∛∜π∑∏∫∂∞∠△⊥∥]/;
  const DIGITS = /[0-9\u06F0-\u06F9\u0660-\u0669]/;

  const blocks = container.querySelectorAll(
    "p, h1, h2, h3, td, th, li, blockquote",
  );
  blocks.forEach((block) => {
    // عبور از بلوک‌هایی که قبلاً دکور شده‌اند
    if (block.querySelector(".math-expression-wrap")) return;

    const childNodes = Array.from(block.childNodes);
    let currentGroup = [];

    const flushGroup = () => {
      if (currentGroup.length === 0) return;

      let hasMathElement = false;
      for (const item of currentGroup) {
        if (item.type === "MATH_NODE") hasMathElement = true;
        if (item.type === "TEXT") {
          if (OPERATORS.test(item.text) || /[a-zA-Z]/.test(item.text))
            hasMathElement = true;
        }
      }

      if (hasMathElement && currentGroup.length > 0) {
        const wrapper = document.createElement("span");
        wrapper.className = "math-expression-wrap";
        wrapper.setAttribute("dir", "ltr");
        wrapper.style.cssText =
          "direction: ltr; unicode-bidi: isolate; display: inline-block; vertical-align: baseline; margin: 0 4px;";

        const firstNode = currentGroup[0].node;
        block.insertBefore(wrapper, firstNode);

        for (const item of currentGroup) {
          wrapper.appendChild(item.node);
        }
      }

      currentGroup = [];
    };

    for (const node of childNodes) {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node.classList.contains("math-formula-container") ||
          node.hasAttribute("data-type") ||
          node.classList.contains("katex"))
      ) {
        currentGroup.push({ type: "MATH_NODE", node });
      } else if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue;
        if (FA_LETTERS.test(text)) {
          // متن شامل حروف فارسی است؛ تقسیم به بخش قبل از فارسی و بعد از آن
          const parts = text.split(
            /([\u0621-\u0628\u062A-\u063A\u0641-\u0642\u0644-\u0648\u064E-\u065F\u067E\u0686\u0698\u06A9\u06AF\u06CC\u0679\u0683\u0688\u0691\u06BA\u06D2\u0629\u0649\u064A\u064B\u064C\u064D\u0650\u0651\u0652]+)/,
          );
          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (!part) continue;
            const subNode = document.createTextNode(part);
            block.insertBefore(subNode, node);
            if (FA_LETTERS.test(part)) {
              flushGroup();
            } else {
              currentGroup.push({ type: "TEXT", node: subNode, text: part });
            }
          }
          block.removeChild(node);
        } else {
          currentGroup.push({ type: "TEXT", node, text });
        }
      } else {
        flushGroup();
      }
    }

    flushGroup();
  });
};

/**
 * همه فرمول‌های موجود در HTML را به خروجی استاتیک KaTeX تبدیل می‌کند.
 * فرمول‌های ادیتور (math / fraction / sqrt / power / ...) همگی data-latex دارند؛
 * کافی است محتوای داخل هر container با رندر KaTeX جایگزین شود.
 *
 * + مهاجرت تصاویر قدیمی: اگر width درصدی روی خود img بود، به کانتینر منتقل می‌شود
 *   (باگ لایوت سایت اصلی — عرض درصدی نسبت به والد inline-block بی‌معنا بود).
 */
const renderAllFormulas = (html) => {
  if (typeof document === "undefined") return html;
  const host = document.createElement("div");
  host.innerHTML = html;

  host.querySelectorAll(".math-formula-container[data-latex]").forEach((el) => {
    const latex = el.getAttribute("data-latex");
    if (!latex || el.dataset.rendered === "true") return;
    const cleanLatex = latex.replace(/\\frac(?![a-zA-Z])/g, "\\dfrac");
    el.innerHTML = renderLatexToHtml(cleanLatex);
    el.dataset.rendered = "true";
  });

  // فرمول‌های درون‌خطی جدید (fraction/sqrt/power/...) که فقط attribute دارند
  host
    .querySelectorAll(
      "span[data-type][data-latex]:not(.math-formula-container)",
    )
    .forEach((el) => {
      const latex = el.getAttribute("data-latex");
      if (!latex || el.dataset.rendered === "true") return;
      el.classList.add("math-formula-container");
      el.removeAttribute("style");
      el.style.cssText =
        "display:inline-block;direction:ltr;unicode-bidi:isolate;vertical-align:middle;margin:0 4px;line-height:normal;";
      const cleanLatex = latex.replace(/\\frac(?![a-zA-Z])/g, "\\dfrac");
      el.innerHTML = renderLatexToHtml(cleanLatex);
      el.dataset.rendered = "true";
    });

  // گروه‌بندی عبارات ریاضی جهت چیدمان یکپارچه چپ‌به‌راست (LTR)
  wrapMathExpressionsInDom(host);

  // تبدیل ارقام لاتین معمولی به ارقام فارسی در کل متن
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
  let textNode;
  while ((textNode = walker.nextNode())) {
    if (textNode.parentElement?.closest(".katex")) continue;
    if (/[0-9]/.test(textNode.nodeValue)) {
      textNode.nodeValue = textNode.nodeValue.replace(/[0-9]/g, (d) =>
        String.fromCharCode(d.charCodeAt(0) + 1728),
      );
    }
  }

  // ─── مهاجرت تصاویر قدیمی: width از img → کانتینر ───
  host.querySelectorAll(".exam-image-wrapper").forEach((wrapper) => {
    const img = wrapper.querySelector("img");
    if (!img) return;

    // اگر data-width روی wrapper هست ولی style.width ندارد → اعمال کن
    // (نکته: max-width نباید به عنوان width شمرده شود — با word boundary چک می‌کنیم)
    const dw = wrapper.getAttribute("data-width");
    const wrapperStyle = wrapper.getAttribute("style") || "";
    if (dw && !/(^|[^-])width\s*:/.test(wrapperStyle)) {
      wrapper.style.width = dw;
      wrapper.style.maxWidth = "100%";
    }

    // اگر img هنوز width درصدی خودش را دارد → پاک شود (کانتینر حاکم است)
    const imgW = img.style.width;
    if (imgW && imgW.includes("%") && imgW !== "100%") {
      img.style.width = "100%";
    }
    img.style.display = "block";
  });

  return host.innerHTML;
};

/**
 * HTML خام ادیتور را به HTML خوداتکا تبدیل می‌کند.
 * @param {string} rawHtml خروجی editor.getHTML()
 * @returns {string} HTML آماده ذخیره/نمایش هر جا
 */
export const buildSelfContainedHtml = (rawHtml) => {
  let content = rawHtml || "";
  if (typeof document !== "undefined") {
    const host = document.createElement("div");
    host.innerHTML = content;
    // حذف استایل‌های تکراری و باز کردن کانتینرهای قبلی اگر وجود داشت
    host
      .querySelectorAll("style[data-exam-styles]")
      .forEach((el) => el.remove());
    host.querySelectorAll(".exam-question-content").forEach((wrap) => {
      const parent = wrap.parentNode;
      if (parent) {
        while (wrap.firstChild) parent.insertBefore(wrap.firstChild, wrap);
        parent.removeChild(wrap);
      }
    });
    content = host.innerHTML;
  }
  const rendered = renderAllFormulas(content);
  return `<div class="exam-question-content">${BASE_STYLE}\n${rendered}</div>`;
};

/** نسخه سبک: فقط wrapper بدون style (برای وقتی سایت اصلی خودش KaTeX دارد) */
export const buildPlainHtml = (rawHtml) =>
  `<div class="exam-question-content">${renderAllFormulas(rawHtml || "")}</div>`;
